import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Input } from '../ui/components';
import { Icon } from '../ui/icons';
import { useAuth } from '../auth/AuthContext';
import { auth as authApi } from '../lib/api';
import { isPasskeySupported, getPasskeyAssertion } from '../lib/webauthn';
import { useT } from '../i18n';
import './LoginPage.css';

const FEATURES = [
  { icon: Icon.ShoppingCart, label: 'Procure-to-pay, purchase orders & approvals' },
  { icon: Icon.Boxes, label: 'Real-time inventory across every location' },
  { icon: Icon.Plug, label: 'Native to the Prostay & TableView ecosystem' },
];

export default function LoginPage() {
  const { login, loginWithAuthResult } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const handlePasskey = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      const { sessionId, options } = await authApi.passkeyLoginOptions();
      const assertion = await getPasskeyAssertion(options);
      const result = await authApi.passkeyLogin(sessionId, assertion);
      loginWithAuthResult(result);
      navigate(from, { replace: true });
    } catch (err) {
      // The user closing the browser prompt is not an error worth showing.
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        setError(err?.message || t('Passkey sign-in failed.'));
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError(t('Please enter your email and password.'));
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.status === 401 ? t('Invalid email or password.') : err?.message || t('Sign in failed.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ps-scope pp-login">
      <aside className="pp-login__brand">
        <div className="pp-login__logo">
          <span className="pp-login__logo-mark">P</span>
          <span>Prostay Procurement</span>
        </div>

        <div className="pp-login__pitch">
          <h1 className="pp-login__headline">{t('Advanced stock & procurement, built into your stack.')}</h1>
          <p className="pp-login__sub">
            {t('Source, purchase, receive and track inventory across all your properties — fully interconnected with Prostay and TableView.')}
          </p>
        </div>

        <div className="pp-login__features">
          {FEATURES.map((f) => (
            <div className="pp-login__feature" key={f.label}>
              <span className="pp-login__feature-dot">
                <f.icon size={16} />
              </span>
              <span>{t(f.label)}</span>
            </div>
          ))}
        </div>
      </aside>

      <section className="pp-login__panel">
        <div className="pp-login__card">
          <h2 className="pp-login__title">{t('Welcome back')}</h2>
          <p className="pp-login__desc">{t('Sign in to your procurement workspace.')}</p>

          <form className="pp-login__form" onSubmit={handleSubmit}>
            {error && (
              <div className="pp-login__error" role="alert">
                <Icon.CircleAlert size={16} />
                <span>{error}</span>
              </div>
            )}

            <Input
              label={t('Email')}
              type="email"
              autoFocus
              autoComplete="username"
              placeholder="you@company.com"
              leadingIcon={<Icon.Mail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Input
              label={t('Password')}
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              leadingIcon={<Icon.Lock size={16} />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <div className="pp-login__forgot">
              <Link to="/forgot-password" className="pp-login__link">{t('Forgot password?')}</Link>
            </div>

            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
              {t('Sign in')}
            </Button>

            {isPasskeySupported() && (
              <>
                <div className="pp-login__divider"><span>{t('or')}</span></div>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  fullWidth
                  loading={passkeyLoading}
                  onClick={handlePasskey}
                >
                  <Icon.Fingerprint size={18} />
                  {t('Sign in with passkey')}
                </Button>
              </>
            )}
          </form>

          <p className="pp-login__foot">{t('Prostay Procurement & Inventory · Secure access')}</p>
        </div>
      </section>
    </div>
  );
}
